import apiService from "./api";

export interface RitualType {
	id: string;
	name: string;
	code: string;
	description?: string;
	spiritualLineId: string;
	isActive: boolean;
	createdAt: string;
	updatedAt: string;
	spiritualLine?: {
		id: string;
		name: string;
		icon?: string;
		primaryColor?: string;
	};
	attendanceCards?: {
		id: string;
		attendanceTypeId: string;
		maxTickets?: number;
		sortOrder: number;
		isActive: boolean;
		attendanceType: {
			id: string;
			code: string;
			label: string;
			numberingMode: string;
			maxTickets?: number;
			hasQueue?: boolean;
			tracksTime?: boolean;
			medium?: { id: string; firstName: string; lastName: string; nickname?: string };
			guide?: { id: string; name: string };
		};
		_count?: { tickets: number };
	}[];
}

export interface CreateRitualTypeData {
	name: string;
	code: string;
	description?: string;
	spiritualLineId: string;
	isActive?: boolean;
}

export interface UpdateRitualTypeData {
	name?: string;
	code?: string;
	description?: string;
	spiritualLineId?: string;
	isActive?: boolean;
}

export interface DeleteRitualTypeResponse {
	action: "deleted" | "deactivated";
	data?: RitualType;
	dependencies?: {
		rituals: number;
		attendanceCards: number;
		pendingReturns: number;
	};
}

export interface FindRitualTypesParams {
	page?: number;
	limit?: number;
	search?: string;
	spiritualLineId?: string;
	isActive?: boolean;
	sortBy?: string;
	sortOrder?: "asc" | "desc";
}

export interface PaginatedRitualTypes {
	data: RitualType[];
	meta: {
		total: number;
		page: number;
		limit: number;
		pages: number;
	};
}

/**
 * Serviço para gerenciar tipos de rituais
 */
export default {
	/**
	 * Obtém lista de tipos de rituais com filtros e paginação
	 */
	async getRitualTypes(
		params?: FindRitualTypesParams
	): Promise<PaginatedRitualTypes> {
		const response = await apiService.get("/ritual-types", { params });
		return response;
	},

	/**
	 * Obtém um tipo de ritual específico por ID
	 */
	async getRitualTypeById(id: string): Promise<RitualType> {
		const response = await apiService.get(`/ritual-types/${id}`);
		return response;
	},

	/**
	 * Cria um novo tipo de ritual
	 */
	async createRitualType(data: CreateRitualTypeData): Promise<RitualType> {
		const response = await apiService.post("/ritual-types", data);
		return response;
	},

	/**
	 * Atualiza um tipo de ritual existente
	 */
	async updateRitualType(
		id: string,
		data: UpdateRitualTypeData
	): Promise<RitualType> {
		const response = await apiService.put(`/ritual-types/${id}`, data);
		return response;
	},

	/**
	 * Alterna o status ativo/inativo de um tipo de ritual
	 */
	async toggleRitualTypeStatus(id: string): Promise<RitualType> {
		const response = await apiService.patch(
			`/ritual-types/${id}/toggle-active`
		);
		return response;
	},

	/**
	 * Exclui um tipo de ritual (hard delete se sem dependências, senão desativa)
	 */
	async deleteRitualType(id: string): Promise<DeleteRitualTypeResponse> {
		const response = await apiService.delete(`/ritual-types/${id}`);
		return response;
	},

	/**
	 * Obtém todos os tipos de rituais ativos (para uso em selects)
	 */
	async getActiveRitualTypes(): Promise<RitualType[]> {
		const response = await apiService.get("/ritual-types", {
			params: { isActive: true, limit: 100 },
		});
		return response.data;
	},

	/**
	 * Obtém estatísticas dos tipos de rituais
	 */
	async getRitualTypeStats(): Promise<{
		total: number;
		active: number;
		inactive: number;
		withCards: number;
	}> {
		const response = await apiService.get("/ritual-types/stats");
		return response;
	},
};
